import React, {
  ComponentType,
  FormEventHandler,
  KeyboardEventHandler,
  MouseEventHandler
} from "react";

import { StyledComponent } from "styled-components";

import { styledWithConfig } from "./utils";

import type { Styles } from "./types";

const components = {
  a: styledWithConfig("a"),
  div: styledWithConfig("div"),
  h1: styledWithConfig("h1"),
  h2: styledWithConfig("h2"),
  h3: styledWithConfig("h3"),
  h4: styledWithConfig("h4"),
  h5: styledWithConfig("h5"),
  button: styledWithConfig("button"),
  p: styledWithConfig("p"),
  span: styledWithConfig("span"),
  label: styledWithConfig("label"),
  form: styledWithConfig("form"),
  input: styledWithConfig("input")
};

type Component = keyof typeof components;

type ComponentTypes =
  | { customComponent: true; component: ComponentType }
  | { customComponent?: false; component?: Component };

type OptionalA11yProps = {
  "aria-label"?: string;
  "aria-hidden"?: boolean;
  UNSAFE_ariaChecked?: boolean;
  UNSAFE_role?: string;
};

export type MakeBoxProps = {
  className?: string;
  children?: React.ReactNode;
  dataNoTranslate?: boolean;
  dataTest?: string;
  onClick?: MouseEventHandler;
  onSubmit?: FormEventHandler;
  disabled?: boolean;
  htmlFor?: string;
  id?: string;
  placeholder?: string;
  type?: string;
  tabindex?: number;
  onKeyDown?: KeyboardEventHandler;
  autoFocus?: boolean;
  onChange?: FormEventHandler;
  value?: string;
  checked?: boolean;
  name?: string;
  title?: string;
} & Styles &
  OptionalA11yProps;

type Props = MakeBoxProps & ComponentTypes;

const Box = React.forwardRef<any, Props>((props, ref) => {
  const {
    component = "div",
    children,
    dataNoTranslate,
    dataTest,
    customComponent,
    UNSAFE_role,
    UNSAFE_ariaChecked,
    ...otherProps
  } = props;
  const Component = customComponent
    ? (component as ComponentType)
    : (components[component as Component] as StyledComponent<Component, any>);

  const dataAttributes = {
    "data-notranslate": dataNoTranslate,
    "data-test": dataTest
  };

  const ariaAttributes = {
    role: UNSAFE_role,
    "aria-checked": UNSAFE_ariaChecked
  };

  return (
    <Component
      ref={ref}
      {...otherProps}
      {...dataAttributes}
      {...ariaAttributes}
    >
      {children}
    </Component>
  );
});

export function makeBox(
  comp: React.ReactNode | keyof JSX.IntrinsicElements | React.ComponentType<any>
) {
  const component = styledWithConfig(
    comp as keyof JSX.IntrinsicElements | React.ComponentType<any>
  );

  return function _Box(props: MakeBoxProps) {
    return <Box {...props} customComponent component={component} />;
  };
}

export default Box;
